import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcessFlowchartSpec,
  NodeDetailsSpec,
  ProcessFlowchartNodeConfigSpec,
  ProcessFlowchartEdgeConfigSpec,
} from 'processes/dao/ProcessSchemaDAO';
import {isNodeWithInboundSchemaMappers} from 'processes/graph-builder/flowchart-viz/util/isNodeWithInboundSchemaMappers';
import getSchemaMapperConfigsMappingToNode from 'processes/graph-builder-processes/util/getSchemaMapperConfigsMappingToNode';
import {
  ProcessTaskSchemaForGraphBuilderValidation,
  SchemaMapperConfig,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {convertToRouterConfigSpec} from 'processes/modals/router-config/RouterConfigUtils';
import {assertNever} from 'util/Types';

import {ProcessFlowchartInteractiveNode, ProcessInteractiveFlowchart} from '../ProcessFlowchartVizConstants';
import {getClientIdForProcessFlowchartEdge} from './ConvertProcessFlowchartForReactFlowUtil';
import findUpstreamTaskNodes from './findUpstreamTaskNodes';
import getSchemaMappersMappingToNode from './getSchemaMappersMappingToNode';

function getNodeDetailsFromInteractiveNode(
  node: ProcessFlowchartInteractiveNode,
  flowchartTaskSchema: ProcessTaskSchemaForGraphBuilderValidation
): NodeDetailsSpec {
  switch (node.type) {
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      return {
        type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
        taskSchemaId: flowchartTaskSchema.id,
      };
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      assert(
        flowchartTaskSchema.outputSchema != null,
        'Expected flowchart with output node to have output schema',
        {node, flowchartTaskSchema}
      );
      return {
        type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
        outputSchemaId: flowchartTaskSchema.outputSchema.id,
      };
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
      return {
        type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
        taskSchemaId: node.taskSchemaId,
      };

    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS: {
      const routerFunctionSpecs = [
        ...node.routerConfig.functionSpecs,
        node.routerConfig.defaultFunctionSpec,
      ].map((functionSpec) =>
        convertToRouterConfigSpec(functionSpec!, /* flowchartSpecHasEdgeToOutput */ true)
      );
      return {
        type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
        name: node.routerConfig.name,
        routerFunctions: routerFunctionSpecs,
      };
    }
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS: {
      return {
        type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
        name: node.name,
      };
    }
    default:
      assert(false, `Unexpected node ${assertNever(node)}`);
  }
}

export default function convertProcessInteractiveFlowchartToFlowchartSpec(
  flowchart: ProcessInteractiveFlowchart,
  schemaMapperConfigs: Array<SchemaMapperConfig>,
  flowchartTaskSchema: ProcessTaskSchemaForGraphBuilderValidation
): ProcessFlowchartSpec {
  const nodeSpecs: Array<ProcessFlowchartNodeConfigSpec> = [];
  for (const node of flowchart.nodes) {
    if (!isNodeWithInboundSchemaMappers(node)) {
      const nodeDetails = getNodeDetailsFromInteractiveNode(node, flowchartTaskSchema);
      nodeSpecs.push({
        clientId: node.clientId,
        nodeDetails,
      });
    } else {
      const upstreamNodesMappingFrom = findUpstreamTaskNodes(node, flowchart);
      const upstreamTaskSchemaIds: Set<string> = new Set(
        upstreamNodesMappingFrom.map(({taskSchemaId}) => taskSchemaId)
      );
      const fulfillingSchemaMapperConfigs = getSchemaMapperConfigsMappingToNode(
        node,
        upstreamTaskSchemaIds,
        schemaMapperConfigs
      );
      assert(fulfillingSchemaMapperConfigs != null, 'Expected node to be fully mapped', {
        flowchart,
        node,
        upstreamTaskSchemaIds,
        schemaMapperConfigs,
      });
      const inboundSchemaMappers = getSchemaMappersMappingToNode({
        taskNode: node,
        upstreamTaskNodes: upstreamNodesMappingFrom,
        fulfillingMapperConfigs: fulfillingSchemaMapperConfigs,
        flowchartTaskSchema,
      });

      const nodeDetails = getNodeDetailsFromInteractiveNode(node, flowchartTaskSchema);

      const mappableNodeSpec = {
        clientId: node.clientId,
        nodeDetails,
        inboundSchemaMappers,
      };

      nodeSpecs.push(mappableNodeSpec);
    }
  }

  const edgeSpecs: Array<ProcessFlowchartEdgeConfigSpec> = flowchart.edges.map(
    ({fromNodeClientId, toNodeClientId}) => ({
      clientId: getClientIdForProcessFlowchartEdge(fromNodeClientId, toNodeClientId),
      fromNodeClientId,
      toNodeClientId,
    })
  );

  return {
    clientId: flowchart.clientId,
    nodes: nodeSpecs,
    edges: edgeSpecs,
  };
}
